<template>
  <div
    class="flex flex-col divide-y divide-border bg-muted overflow-hidden sm:overflow-init text-muted-foreground p-3 rounded">
    <div v-if="!onlyShowTotals">
      <div v-if="!hideSubtotalAndTicketFee" class="flex justify-between py-4 -mt-4">
        <div class="grow">
          {{ $t("Subtotal") }}
        </div>
        <div class="w-40 text-right flex justify-end">
          <Euros data-testid="subtotal" :amount="subtotal" />
        </div>
      </div>
      <div v-if="payDetails?.bookingcosts?.sub_total > 0" class="py-4 flex justify-between">
        <div class="flex-1 whitespace-nowrap">
          {{ $t("Bookingcosts") }}
        </div>
        <div class="flex-2 flex text-base text-right justify-between items-center gap-1">
          <span class="text-right grow">{{ payDetails.bookingcosts.quantity }}</span>
          &times;
          <Euros data-testid="subtotal" :amount="payDetails.bookingcosts.net_rate" />
        </div>
        <div class="flex-1 text-right flex justify-end">
          <Euros :amount="payDetails.bookingcosts.sub_total" />
        </div>
      </div>
      <div v-if="payDetails?.ticket_fee?.sub_total > 0 && !hideSubtotalAndTicketFee" class="py-4 flex justify-between">
        <div class="flex-1 whitespace-nowrap">
          {{ $t("Ticket fee") }}
        </div>
        <div class="flex-2 flex text-base text-right justify-between items-center gap-1">
          <span class="text-right grow">{{ payDetails.ticket_fee.quantity }}</span>
          &times;
          <Euros :amount="payDetails.ticket_fee.net_rate" />
        </div>
        <div class="flex-1 text-right flex justify-end">
          <Euros data-testid="ticketFee" :amount="payDetails.ticket_fee.sub_total" />
        </div>
      </div>
      <div v-if="hasPaymentFee" class="py-4 flex justify-between">
        <div class="whitespace-nowrap">
          {{ $t("Payment fee") }}
        </div>
        <div class="text-right flex justify-end">
          <Euros :amount="payDetails.payment_fee.sub_total" />
        </div>
      </div>
      <div v-if="totalVat" class="py-4 flex flex-col justify-between">
        <div v-if="!hideSubtotalAndTicketFee" class="flex justify-between">
          <div class="grow flex gap-1 cursor-pointer" @click="showVatDetails = !showVatDetails">
            {{ $t("VAT total") }}{{ reverseChargeApplied ? "*" : "" }}
            <ChevronDownIcon :class="`w-4  ${showVatDetails ? 'rotate-180' : ''}`" />
          </div>
          <Euros data-testid="VAT" :amount="totalVat" />
        </div>
        <div v-else>
          <div v-if="payDetails?.payment_fee?.vat" class="flex justify-between">
            <div class="flex gap-1 items-center">
              {{
                $t("VAT {percentage}% on {subject}", {
                  percentage: payDetails.payment_fee.vat_rate,
                  subject: $t("payment fee").toLowerCase(),
                })
              }}
            </div>
            <Euros :amount="payDetails.payment_fee.vat" />
          </div>
        </div>
        <div v-if="showVatDetails && !hideSubtotalAndTicketFee" class="mt-2">
          <template v-for="[, value] in Array.from(vatGroupsTickets?.entries() || [])" :key="value.rate">
            <div v-if="value.amount" class="flex justify-between">
              <div class="flex gap-1 items-center ml-2">
                <ArrowTurnDownRightIcon class="size-3" />
                <div>
                  {{
                    $t("VAT {percentage}% on {subject}", {
                      percentage: value.rate,
                      subject: $t("tickets").toLowerCase(),
                    })
                  }}
                  ({{ Array.from(value.countries).join(", ") }})
                </div>
              </div>
              <Euros :amount="value.amount" />
            </div>
          </template>
          <div v-if="payDetails?.ticket_fee?.vat" class="flex justify-between">
            <div class="flex gap-1 items-center ml-2">
              <ArrowTurnDownRightIcon class="size-3" />
              {{
                $t("VAT {percentage}% on {subject}", {
                  percentage: payDetails.ticket_fee.vat_rate,
                  subject: $t("ticket fee").toLowerCase(),
                })
              }}
            </div>
            <Euros :amount="payDetails.ticket_fee.vat" />
          </div>
          <div v-if="payDetails?.bookingcosts?.vat" class="flex justify-between">
            <div class="flex gap-1 items-center ml-2">
              <ArrowTurnDownRightIcon class="size-3" />
              {{
                $t("VAT {percentage}% on {subject}", {
                  percentage: payDetails.bookingcosts.vat_rate,
                  subject: $t("bookingcosts").toLowerCase(),
                })
              }}
            </div>
            <Euros :amount="payDetails.bookingcosts.vat" />
          </div>
          <div v-if="payDetails?.payment_fee?.vat" class="flex justify-between">
            <div class="flex gap-1 items-center ml-2">
              <ArrowTurnDownRightIcon class="size-3" />
              {{
                $t("VAT {percentage}% on {subject}", {
                  percentage: payDetails.payment_fee.vat_rate,
                  subject: $t("payment fee").toLowerCase(),
                })
              }}
            </div>
            <Euros :amount="payDetails.payment_fee.vat" />
          </div>
        </div>
      </div>
    </div>
    <div class="py-4 font-medium flex justify-between text-xl text-foreground -mb-4">
      <div class="grow">
        {{ $t("Order total") }}
      </div>
      <div class="w-40 text-right flex justify-end">
        <Euros data-testid="totalAmount" :amount="payDetails?.total.sub_total" class="text-xl" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import Euros from "@/components/Euros.vue";
import { computed, onMounted, ref, watch } from "vue";
import { useCartStore } from "@/store/cart.ts";
import { ChevronDownIcon, ArrowTurnDownRightIcon } from "@heroicons/vue/24/outline";

/* eslint-disable @typescript-eslint/no-explicit-any */
const props = withDefaults(
  defineProps<{ payDetails?: any; hideSubtotalAndTicketFee?: boolean; onlyShowTotals?: boolean }>(),
  {
    payDetails: null,
    hideSubtotalAndTicketFee: false,
    onlyShowTotals: false,
  }
);

const cartStore = useCartStore();

const subtotal = computed(() => {
  const details = payDetails.value;
  let total = 0;
  if (details?.tickets) {
    details.tickets.forEach((ticket) => {
      total += ticket.sub_total_exc_vat;
    });
  }
  return total;
});

const showVatDetails = ref(false);

const vatGroupsTickets = computed(() => {
  const grouped = payDetails.value?.tickets.reduce((map, { vat_rate, vat_amount, ticket_id }) => {
    const country = cartStore.items.find((x) => x.ticket.ticket_id == ticket_id)?.event.iso_country;

    if (!vat_amount) return map;

    if (!map.has(vat_rate)) {
      map.set(vat_rate, { amount: 0, rate: vat_rate, countries: new Set() });
    }

    const entry = map.get(vat_rate);
    entry.amount += vat_amount;
    entry.countries.add(country);
    entry.rate = vat_rate;

    return map;
  }, new Map());

  if (!grouped) return;

  return grouped as Map<number, { rate: number; amount: number; countries: Set<string> }>;
});

const totalTicketsVat = computed(() => {
  return (
    (vatGroupsTickets.value &&
      Array.from(vatGroupsTickets.value.values()).reduce((previous, current) => previous + current.amount, 0)) ||
    0
  );
});

const totalVat = computed(() => {
  const ticketFee = payDetails?.value?.ticket_fee?.vat || 0;
  const paymentFee = payDetails?.value?.payment_fee?.vat || 0;
  const bookingcosts = payDetails?.value?.bookingcosts?.vat || 0;

  const ticketVatTotal = totalTicketsVat.value;

  return ticketFee + ticketVatTotal + bookingcosts + paymentFee;
});

const hasPaymentFee = computed(() => {
  if (payDetails.value?.payment_fee) {
    if (payDetails.value?.payment_fee.sub_total > 0) {
      return Object.keys(payDetails.value.payment_fee).length > 0;
    }
  }

  return false;
});

const reverseChargeApplied = computed(() => {
  return payDetails.value?.tickets?.some((x) => x.reverse_charge_applied) ?? false;
});

const payDetails = computed(() => props.payDetails ?? cartStore.payDetails);

onMounted(() => {
  watch(
    () => cartStore.payDetails,
    (value) => {
      payDetails.value = value;
    },
    { deep: true, immediate: true }
  );
});
</script>
